import { Box, BoxProps, Flex, FlexProps, Image } from '@chakra-ui/react';
import { Skeleton } from '@main/complykit/components/skeleton';
import { FileItemRef } from '@nhost/nhost-js';
import { useEffect, useState } from 'react';

import { useFileInputContext } from './context';
import { UploadedFile } from './shared';
import { usePresignedUrl } from './use-presigned-url';

export type FileUploadPreviewProps = {
  width?: BoxProps['width'];
  height?: BoxProps['height'];
  maxHeight?: BoxProps['maxHeight'];
  objectFit?: BoxProps['objectFit'];
  alt?: string;
  gap?: FlexProps['gap'];
  direction?: FlexProps['direction'];
  wrap?: FlexProps['wrap'];
};

/** 
  For the Preview component to work on "uploaded file", file must include @mimeType property 
 */
export const FileUploadPreview = ({
  width = 'full',
  height,
  maxHeight,
  objectFit = 'contain',
  alt = 'Preview',
  gap = 3,
  direction = 'row',
  wrap = 'wrap',
}: FileUploadPreviewProps) => {
  const { stagedFiles = [], uploadedFiles = [] } = useFileInputContext();

  const uploadedImages = uploadedFiles.filter((f) => f.mimeType?.startsWith('image/'));

  const stagedImages = stagedFiles.filter((f) => {
    const file = f.getSnapshot()?.context.file;
    return file?.type.startsWith('image/');
  });

  if (!uploadedImages.length && !stagedImages.length) {
    return null;
  }

  return (
    <Flex gap={gap} direction={direction} wrap={wrap}>
      {uploadedImages.map((file) => (
        <UploadedImagePreview
          key={file.id}
          file={file}
          width={width}
          height={height}
          maxHeight={maxHeight}
          objectFit={objectFit}
          alt={alt}
        />
      ))}
      {stagedImages.map((stagedFile) => (
        <StagedImagePreview
          key={stagedFile.id}
          file={stagedFile}
          width={width}
          height={height}
          maxHeight={maxHeight}
          objectFit={objectFit}
          alt={alt}
        />
      ))}
    </Flex>
  );
};

type PreviewItemProps = {
  width: BoxProps['width'];
  height: BoxProps['height'];
  maxHeight?: BoxProps['maxHeight'];
  objectFit: BoxProps['objectFit'];
  alt: string;
};

type UploadedImagePreviewProps = PreviewItemProps & {
  file: UploadedFile;
};

function UploadedImagePreview({
  file,
  width,
  height,
  maxHeight = `300px`,
  objectFit,
  alt,
}: UploadedImagePreviewProps) {
  const { url, isLoading } = usePresignedUrl(file.id);

  if (isLoading) {
    return (
      <PreviewContainer>
        <Box w={width} maxH={maxHeight} h={height}>
          <Skeleton.Block isFilled />
        </Box>
      </PreviewContainer>
    );
  }

  if (!url) {
    return null;
  }

  return (
    <PreviewContainer>
      <Image src={url} w={width} maxH={maxHeight} h={height} objectFit={objectFit} alt={alt} />
    </PreviewContainer>
  );
}

type StagedImagePreviewProps = PreviewItemProps & {
  file: FileItemRef;
};

function StagedImagePreview({
  file,
  width,
  height,
  maxHeight,
  objectFit,
  alt,
}: StagedImagePreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string>();

  useEffect(() => {
    const stagedFile = file.getSnapshot()?.context.file;
    if (!stagedFile?.type.startsWith('image/')) {
      return;
    }

    const url = URL.createObjectURL(stagedFile);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!previewUrl) {
    return null;
  }

  return (
    <PreviewContainer>
      <Image
        src={previewUrl}
        w={width}
        h={height}
        maxH={maxHeight}
        objectFit={objectFit}
        alt={alt}
      />
    </PreviewContainer>
  );
}

function PreviewContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      border="1px"
      borderStyle="dashed"
      borderColor="gray.200"
      borderRadius="8px"
      p={4}
      display="flex"
      justifyContent="center"
      alignItems="center"
      bgColor="white"
      w="full"
    >
      {children}
    </Box>
  );
}
